import React from 'react';
import { NativeFormElement, NativeFormElementType } from './store';
import * as Yup from 'yup';
import cloneDeep from 'lodash/cloneDeep';
import { NativeFormConfigInputForm } from './config/input-form';
import { NativeFormConfigDropdownForm } from './config/dropdown-form';
import { NativeFormConfigCheckboxesForm } from './config/checkboxes-form';

const ValidationSchemas = {
  getInputSchema: (isRequired: boolean) => {
    const valueField = Yup.string();
    return Yup.object({
      required: Yup.boolean(),
      value: isRequired ? valueField.required() : valueField,
      label: Yup.string().required('Required'),
    });
  },
  getCheckboxSchema: (isRequired: boolean) => {
    const valuesField = Yup.array().of(Yup.string());
    return Yup.object({
      required: Yup.boolean(),
      label: Yup.string().required('Required'),
      values: isRequired ? valuesField.required() : valuesField,
    });
  },
  getDropdownSchema: (isRequired: boolean) => {
    const valueField = Yup.string();
    return Yup.object({
      required: Yup.boolean(),
      label: Yup.string().required('Required'),
      value: isRequired ? valueField.required() : valueField,
    });
  },
};

export const getValidationSchema: (type: NativeFormElementType, isRequired: boolean) => Yup.ObjectSchema = (
  type,
  isRequired
) => {
  if (type === 'input') {
    return ValidationSchemas.getInputSchema(isRequired);
  }

  if (type === 'checkbox') {
    return ValidationSchemas.getCheckboxSchema(isRequired);
  }

  if (type === 'dropdown') {
    return ValidationSchemas.getDropdownSchema(isRequired);
  }

  return Yup.object();
};

export const NativeFormElementConfigFormFactory = <T extends NativeFormElement>(
  {
    type,
    fields,
  }: {
    type: NativeFormElementType;
    fields: NativeFormElement[];
  },
  onUpdate: (fields: NativeFormElement[]) => void
) => {
  const clonedFields = cloneDeep(fields);
  const onSubmit = (values: Partial<NativeFormElement>) => {
    const fieldToUpdate = clonedFields.find((field) => field.id === values.id);
    if (!fieldToUpdate) {
      return;
    }
    const updateFieldIndex = clonedFields.indexOf(fieldToUpdate);
    if (updateFieldIndex === -1) {
      return;
    }
    clonedFields.splice(updateFieldIndex, 1, { ...fieldToUpdate, ...values } as NativeFormElement);
    onUpdate(clonedFields);
  };

  if (type === 'input') {
    return (props: T) => <NativeFormConfigInputForm onSubmit={onSubmit} {...props} />;
  }

  if (type === 'dropdown') {
    return (props: T) => <NativeFormConfigDropdownForm onSubmit={onSubmit} {...props} />;
  }

  if (type === 'checkbox') {
    return (props: T) => <NativeFormConfigCheckboxesForm onSubmit={onSubmit} {...props} />;
  }

  return () => null;
};
